import { canv } from "@/canv/global";
import { getNumberProperty, getStringProperty, isBrowser } from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import type { EmojiStateFixType, EmojiStateFull, EmojiStateMap } from "./types";
import { renderEmojiToCanvas } from "./utils";

let DRAW_ERROR_LOGGED = false;

export class EmojiWidget extends ElementBase {
    constructor(emoji?: string, startX = 0, startY = 0) {
        super();
        this.startX = startX;
        this.startY = startY;
        this.width = 120;
        this.height = 120;

        if (emoji) {
            this.initEmoji(emoji);
        }

        this.updateDims();
    }

    updateDims() {
        if (!isBrowser()) return;

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    initEmoji(emoji: string) {
        this.emoji = emoji;
    }

    draw() {
        try {
            if (this.emoji) {
                renderEmojiToCanvas(this.emoji, this.startX, this.startY, this.width, this.height);
            }
        } catch (e) {
            if (!DRAW_ERROR_LOGGED) {
                DRAW_ERROR_LOGGED = true;
                canv.captureException(e);
            }
        }
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string) {
        if (["sideRight", "sideBottom", "sideLeft", "sideTop"].includes(catchPointer)) return;

        const diff = Math.abs(x) > Math.abs(y) ? x : y;
        if (this.height + diff <= 15) return;

        const widthKoef = this.width / this.height;
        this.height += diff;
        this.width += widthKoef * diff;
        this.updateDims();
    }

    getWidgetOptions() {
        return [];
    }

    getState<T extends EmojiStateFixType>(fixType: T): EmojiStateMap[T] {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as EmojiStateMap[T];
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                } as EmojiStateMap[T];
            case "full":
                return {
                    entityType: "emojiWidget",
                    UID: this.UID,
                    parentUID: this.parentUID,
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    emoji: this.emoji,
                } as EmojiStateMap[T];
        }
    }

    loadState<T extends EmojiStateFixType>(stateObj: EmojiStateMap[T], fixType: T): void {
        switch (fixType) {
            case "move": {
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            }
            case "resize": {
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                break;
            }
            case "full": {
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                this.emoji = getStringProperty("emoji", stateObj);

                this.initEmoji((stateObj as EmojiStateFull).emoji);
                break;
            }
        }

        this.updateDims();
    }

    getFixType(reason: string): EmojiStateFixType {
        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            default:
                return "full";
        }
    }

    constructorName: "emoji" = "emoji";
    emoji = "";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
}
